import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { collabStateManager } from '@databases/collab-state-manager';
import { useFlags } from '@databases/flags';
import MentionIcon from '@databases/icons/migration/mention';
import usePanel from '@databases/panel/usePanel';
import React from 'react';
import { useIntl } from 'react-intl-next';

import { getConfluenceService } from '@canvas/atlassian-service/confluence';
import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import { messages } from './i18n';
import { buildFlagForMentions } from './mentionBuilder';
import { identifyNotificationEligibleUsers } from './notificationEligibleUsers';
import {
  type CreateMentionNotificationProps,
  MentionFlagType,
  type UseMentionNotificationProps,
} from './types';

export const createMentionNotification = async ({
  contentId,
  mentions,
  fieldId,
  formatMessage,
  addFlag,
  showFlag,
  showPanel,
  analyticsClient,
  isLocked,
}: {
  contentId: string;
  mentions: {
    mentionLocalId: string;
    mentionedUserAccountId: string;
    displayName: string;
  }[];
  fieldId: string;
  formatMessage: (message: any) => string;
  addFlag: (flag: any) => void;
  showFlag: (flag: any) => void;
  showPanel: (panel: any) => void;
  analyticsClient: any;
  isLocked: boolean;
}) => {
  const { usersToMention, usersWithoutAccess, usersWithAccess } =
    await identifyNotificationEligibleUsers({
      contentId,
      mentions,
    });

  const service = getConfluenceService();

  try {
    await service.createMentionNotification({
      contentId,
      mentions: usersToMention,
    });

    const flagData = buildFlagForMentions({
      formatMessage,
      usersWithAccess,
      usersWithoutAccess,
      isLocked,
      showPanel,
      fieldId,
      analyticsClient,
      contentId,
    });

    if (isFeatureFlagEnabled('database-frontend-flags-over-bridge')) {
      flagData.forEach(showFlag);
    } else {
      flagData.forEach(addFlag);
    }

    analyticsClient.sendMentionNotificationFlagViewedEvent({
      contentId,
      databaseContainer: getDatabaseContainerType(),
      type: MentionFlagType.SUCCESS,
    });
    // eslint-disable-next-line canvas/no-swallow-errors-in-catch
  } catch (err) {
    if (isFeatureFlagEnabled('database-frontend-flags-over-bridge')) {
      showFlag({
        id: 'mention-notification-error-flag',
        title: formatMessage(messages['mention-notification.flag-error-title']),
        description: formatMessage(
          messages['mention-notification.flag-error-description']
        ),
        type: 'custom',
        iconString: 'mention',
        close: 'auto',
      });
    } else {
      addFlag({
        title: formatMessage(messages['mention-notification.flag-error-title']),
        description: formatMessage(
          messages['mention-notification.flag-error-description']
        ),
        icon: () => <MentionIcon label="" />,
      });
    }

    analyticsClient.sendMentionNotificationFlagViewedEvent({
      contentId,
      databaseContainer: getDatabaseContainerType(),
      type: MentionFlagType.ERROR,
    });
  }
};
type mentionByFields = {
  [key: string]: CreateMentionNotificationProps['mentions'];
};

const mentionsDict: mentionByFields = {};
export const useMentionNotification = ({ contentId }: UseMentionNotificationProps) => {
  const { formatMessage } = useIntl();
  const { addFlag, showFlag } = useFlags();
  const { showPanel } = usePanel();
  const analyticsClient = useAnalytics();
  const collabManager = collabStateManager.getCollabManager(contentId);
  const ydb = collabManager?.database.toJSON();
  // Users are not allowed to access field settings in a locked structure db
  const isLocked = ydb?.isLocked || false;
  const addOrUpdateEntry = (
    key: string,
    value: CreateMentionNotificationProps['mentions']
  ) => {
    mentionsDict[key] = [...(mentionsDict?.[key] || []), ...value];
  };
  const clearFieldMentions = (key: string) => {
    delete mentionsDict[key];
  };

  const flushMentions = async () => {
    const mentionsRequests: Promise<void>[] = [];
    Object.keys(mentionsDict).forEach(async (fieldId) => {
      if (mentionsDict[fieldId].length > 0) {
        mentionsRequests.push(
          createMentionNotification({
            contentId,
            mentions: [...mentionsDict[fieldId]],
            fieldId,
            formatMessage,
            addFlag,
            showFlag,
            showPanel,
            analyticsClient,
            isLocked,
          })
        );
        clearFieldMentions(fieldId);
      }
    });
    await Promise.all(mentionsRequests);
  };

  const scheduleMentionNotification = ({
    mentions: newMentions,
    fieldId,
  }: CreateMentionNotificationProps) => {
    addOrUpdateEntry(fieldId, newMentions);
  };

  const createMentionNotificationFromHook = async ({
    mentions,
    fieldId,
  }: CreateMentionNotificationProps) => {
    await createMentionNotification({
      contentId,
      mentions,
      fieldId,
      formatMessage,
      addFlag,
      showFlag,
      showPanel,
      analyticsClient,
      isLocked,
    });
  };

  return {
    createMentionNotification: createMentionNotificationFromHook,
    scheduleMentionNotification,
    flushMentions,
  };
};
